import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<string, string>}
   */
  pages = new Map();

  /** @type {string} */
  #pageCacheNamespace = '';

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  // -------------------------
  // Minimal additions
  // -------------------------
  #isIOS = false;

  // prevent double renders + show loader correctly
  #loading = { next: false, previous: false };
  #viewMoreIntersecting = { next: false, previous: false };
  #loaderEl = null;
  #loaderCount = 0;
  #scrollRafId = null;
  #lastRenderAt = 0;
  #minRenderGapMsDesktop = 130;
  #minRenderGapMsIOS = 220;
  #lastRenderedPage = 0;
  #renderedProductIds = new Set();
  #renderedPages = new Set();
  #pendingPageFetches = new Map();
  #prefetchingNextPages = false;
  #contextVersion = 0;
  #lastFetchAt = 0;
  #fetchBackoffUntil = 0;

  // rootMargin tuning per device
  #minObserverMarginPxDesktop = 1400;
  #minObserverMarginPxIOS = 350;
  #observerMarginMultiplierDesktop = 1.8;
  #observerMarginMultiplierIOS = 0.8;
  #hoverDisableThreshold = 160;
  #fetchGapMsDesktop = 250;
  #fetchGapMsIOS = 750;
  #fetchRetryDelayMs = 1200;
  #prefetchPagesAheadDesktop = 4;
  #prefetchPagesAheadIOS = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#isIOS = this.#detectIOS();
    this.#pageCacheNamespace = this.#getPageCacheNamespace();
    this.#lastRenderedPage = this.#getCurrentMaxPage();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#ensureLoader();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    requestIdleCallback(() => this.#prefetchNextPages());
    this.#observeViewMore();
    this.#updateHeavyCardsFlag();
    this.#seedRenderedState();

    // Keep rootMargin correct after viewport changes
    window.addEventListener('resize', this.#onViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.#onViewportChange);
    window.addEventListener('scroll', this.#onWindowScroll, { passive: true });
    window.addEventListener('pageshow', this.#onPageShow);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('resize', this.#onViewportChange);
    window.removeEventListener('orientationchange', this.#onViewportChange);
    window.removeEventListener('scroll', this.#onWindowScroll);
    window.removeEventListener('pageshow', this.#onPageShow);

    if (this.#scrollRafId != null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    this.#loaderEl?.remove?.();
    this.#loaderEl = null;
    this.#loaderCount = 0;
    this.#loading.next = false;
    this.#loading.previous = false;
    this.#viewMoreIntersecting.next = false;
    this.#viewMoreIntersecting.previous = false;
    this.#renderedProductIds.clear();
    this.#renderedPages.clear();
    this.#pendingPageFetches.clear();
    this.#prefetchingNextPages = false;
  }

  // -------------------------
  // Loader
  // -------------------------
  #ensureLoader() {
    const existing = this.querySelector('.pl-loader');
    if (existing) {
      this.#loaderEl = existing;
      return;
    }

    const loader = document.createElement('div');
    loader.className = 'pl-loader';
    loader.setAttribute('aria-hidden', 'true');

    const grid = this.refs?.grid || this.querySelector('[ref="grid"]');
    if (grid?.parentNode) {
      grid.parentNode.insertBefore(loader, grid.nextSibling);
    } else {
      this.appendChild(loader);
    }

    this.#loaderEl = loader;
  }

  #showLoader() {
    this.#ensureLoader();
    this.#loaderCount += 1;
    this.#loaderEl?.classList.add('is-visible');
  }

  #hideLoader() {
    this.#loaderCount = Math.max(0, this.#loaderCount - 1);
    if (this.#loaderCount === 0) this.#loaderEl?.classList.remove('is-visible');
  }

  // -------------------------
  // Viewport/rootMargin
  // -------------------------
  #onViewportChange = () => {
    if (!this.isConnected) return;
    this.#observeViewMore(); // recreate observer with new margin
  };

  #onWindowScroll = () => {
    if (this.#scrollRafId != null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#runAutoloadCheck();
    });
  };

  #onPageShow = () => {
    // When returning via bfcache, IntersectionObserver can be stale.
    if (!this.isConnected) return;
    // Refresh refs in case the DOM was restored from cache.
    this.updatedCallback();

    this.#loading.next = false;
    this.#loading.previous = false;
    this.#viewMoreIntersecting.next = false;
    this.#viewMoreIntersecting.previous = false;
    this.#lastRenderAt = 0;

    this.#observeViewMore();
    requestIdleCallback(() => {
      this.#runAutoloadCheck();
    });
  };

  #runAutoloadCheck() {
    if (!this.isConnected) return;
    if (this.#loading.next || this.#loading.previous) return;
    if (!this.#canRenderNow()) return;

    if (this.#shouldAutoloadNextPage()) {
      this.#renderNextPage();
      return;
    }

    if (this.#shouldAutoloadPreviousPage()) {
      this.#renderPreviousPage();
    }
  }

  #getObserverMarginPx() {
    const h = window.innerHeight || 800;
    const mult = this.#isIOS ? this.#observerMarginMultiplierIOS : this.#observerMarginMultiplierDesktop;
    const min = this.#isIOS ? this.#minObserverMarginPxIOS : this.#minObserverMarginPxDesktop;
    return Math.round(Math.max(h * mult, min));
  }

  #getRenderGapMs() {
    return this.#isIOS ? this.#minRenderGapMsIOS : this.#minRenderGapMsDesktop;
  }

  #canRenderNow() {
    return performance.now() - this.#lastRenderAt >= this.#getRenderGapMs();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Always rebuild so rootMargin stays accurate
    this.infinityScrollObserver?.disconnect();
    this.#viewMoreIntersecting.next = false;
    this.#viewMoreIntersecting.previous = false;

    const margin = this.#getObserverMarginPx();

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          const { viewMorePrevious, viewMoreNext } = this.refs;

          if (entry.target === viewMorePrevious) {
            this.#viewMoreIntersecting.previous = entry.isIntersecting;
          } else if (entry.target === viewMoreNext) {
            this.#viewMoreIntersecting.next = entry.isIntersecting;
          }

          if (!entry.isIntersecting) continue;

          // avoid overlapping renders
          if (this.#loading.next || this.#loading.previous) continue;

          if (!this.#canRenderNow()) continue;

          if (entry.target === viewMorePrevious) {
            this.#renderPreviousPage();
          } else if (entry.target === viewMoreNext) {
            this.#renderNextPage();
          }
        }
      },
      {
        rootMargin: `${margin}px 0px`,
      }
    );

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.warn('[PaginatedList] Failed to fetch page', page.page, error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageCacheNamespace = this.#getPageCacheNamespace(pageInfo.url);
    if (this.#pageCacheNamespace !== pageCacheNamespace) {
      this.pages.clear();
      this.#pendingPageFetches.clear();
      this.#contextVersion += 1;
      this.#pageCacheNamespace = pageCacheNamespace;
    }

    const pageCacheKey = this.#getPageCacheKey(pageNumber, pageCacheNamespace);
    if (this.pages.has(pageCacheKey)) return;

    const pendingFetch = this.#pendingPageFetches.get(pageCacheKey);
    if (pendingFetch) {
      await pendingFetch;
      return;
    }

    const contextVersion = this.#contextVersion;
    const fetchPromise = this.#fetchAndStorePage(pageInfo.url, pageCacheKey, contextVersion);
    this.#pendingPageFetches.set(pageCacheKey, fetchPromise);

    try {
      await fetchPromise;
    } finally {
      this.#pendingPageFetches.delete(pageCacheKey);
    }
  }

  async #prefetchNextPages() {
    if (this.#prefetchingNextPages) return;
    if (!this.isConnected) return;

    const currentMaxPage = this.#getCurrentMaxPage();
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    if (!currentMaxPage || !Number.isFinite(lastPage)) return;

    const pagesAhead = this.#isIOS ? this.#prefetchPagesAheadIOS : this.#prefetchPagesAheadDesktop;
    const maxPageToFetch = Math.min(lastPage, currentMaxPage + pagesAhead);

    this.#prefetchingNextPages = true;
    const contextVersion = this.#contextVersion;

    try {
      for (let page = currentMaxPage + 1; page <= maxPageToFetch; page += 1) {
        if (!this.isConnected) return;
        if (contextVersion !== this.#contextVersion) return;

        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        url.hash = '';

        await this.#fetchSpecificPage(page, url);
        await yieldToMainThread();
      }
    } finally {
      this.#prefetchingNextPages = false;
    }
  }

  /**
   * @param {URL} url
   * @param {string} pageCacheKey
   * @param {number} contextVersion
   */
  async #fetchAndStorePage(url, pageCacheKey, contextVersion) {
    await this.#waitForFetchSlot();
    if (contextVersion !== this.#contextVersion) return;

    const pageContent = await this.#getSectionPageHTML(url);
    if (contextVersion !== this.#contextVersion) return;
    if (!this.#isValidPageHTML(pageContent)) return;

    this.pages.set(pageCacheKey, pageContent);
  }

  /**
   * Fetch section HTML with one uncached retry. Shopify can occasionally return
   * transient 5xx/429 responses for section rendering during fast scrolls.
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async #getSectionPageHTML(url) {
    try {
      return await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    } catch (error) {
      console.warn('[PaginatedList] Section render failed, retrying without cache', error);
    }

    this.#fetchBackoffUntil = Math.max(this.#fetchBackoffUntil, performance.now() + this.#fetchRetryDelayMs);
    await this.#sleep(this.#fetchRetryDelayMs);

    const retryUrl = new URL(url.toString());
    retryUrl.searchParams.set('_retry', Date.now().toString());
    return sectionRenderer.getSectionHTML(this.sectionId, false, retryUrl);
  }

  async #waitForFetchSlot() {
    const now = performance.now();
    const minGap = this.#isIOS ? this.#fetchGapMsIOS : this.#fetchGapMsDesktop;
    const nextAllowedAt = Math.max(this.#lastFetchAt + minGap, this.#fetchBackoffUntil);
    const waitMs = nextAllowedAt - now;

    if (waitMs > 0) {
      await this.#sleep(waitMs);
    }

    this.#lastFetchAt = performance.now();
  }

  /**
   * @param {number} ms
   * @returns {Promise<void>}
   */
  #sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * @param {string} pageHTML
   * @returns {boolean}
   */
  #isValidPageHTML(pageHTML) {
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    return Boolean(gridElement && gridElement.querySelector('[ref="cards[]"]'));
  }

  async #renderNextPage() {
    if (this.#loading.next) return;
    if (!this.#canRenderNow()) return;
    this.#loading.next = true;
    this.#lastRenderAt = performance.now();
    this.#showLoader();

    try {
      const { grid } = this.refs;
      if (!grid) return;

      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      if (this.#lastRenderedPage >= nextPage.page) return;
      if (this.#renderedPages.has(nextPage.page)) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const deduped = this.#dedupeAndTrack(nextPageItemElements);
      if (!deduped.length) return;
      grid.append(...deduped);
      this.#lastRenderedPage = Math.max(this.#lastRenderedPage, nextPage.page);
      this.#renderedPages.add(nextPage.page);

      await yieldToMainThread();
      this.#aspectRatioHelper.processNewElements();
      this.#updateHeavyCardsFlag();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#prefetchNextPages();
      });
    } finally {
      this.#loading.next = false;
      this.#hideLoader();

      // If sentinel stayed intersecting while we were loading, IntersectionObserver
      // may not emit another entry. Continue loading explicitly.
      if (this.#viewMoreIntersecting.next && this.#shouldUsePage(this.#getPage('next'))) {
        requestIdleCallback(() => {
          this.#renderNextPage();
        });
        return;
      }

      // If the user has already scrolled past the current end of the grid,
      // continue loading without waiting for a new IntersectionObserver event.
      if (this.#shouldAutoloadNextPage()) {
        requestIdleCallback(() => {
          this.#renderNextPage();
        });
      }
    }
  }

  async #renderPreviousPage() {
    if (this.#loading.previous) return;
    if (!this.#canRenderNow()) return;
    this.#loading.previous = true;
    this.#lastRenderAt = performance.now();
    this.#showLoader();

    try {
      const { grid } = this.refs;
      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      if (this.#lastRenderedPage && previousPage.page >= this.#lastRenderedPage) {
        // We already rendered this page in the current context.
        return;
      }
      if (this.#renderedPages.has(previousPage.page)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      const deduped = this.#dedupeAndTrack(previousPageItemElements);
      if (!deduped.length) return;
      grid.prepend(...deduped);
      this.#lastRenderedPage = Math.max(this.#lastRenderedPage, previousPage.page);
      this.#renderedPages.add(previousPage.page);

      await yieldToMainThread();
      this.#aspectRatioHelper.processNewElements();
      this.#updateHeavyCardsFlag();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;

        // iOS-safe behavior (instant is not standard)
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'auto',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#loading.previous = false;
      this.#hideLoader();

      if (this.#viewMoreIntersecting.previous && this.#shouldUsePage(this.#getPage('previous'))) {
        requestIdleCallback(() => {
          this.#renderPreviousPage();
        });
        return;
      }

      // Mirror next-page behavior when the user is already above the loaded range.
      if (this.#shouldAutoloadPreviousPage()) {
        requestIdleCallback(() => {
          this.#renderPreviousPage();
        });
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    const currentCardPage = isPrevious
      ? this.#getCurrentMinPage()
      : this.#getCurrentMaxPage();
    if (!currentCardPage) return;
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Find the lowest page number currently rendered.
   * This avoids relying on refs timing.
   * @returns {number}
   */
  #getCurrentMinPage() {
    const grid = this.refs?.grid || this.querySelector('[ref="grid"]');
    if (!grid) return 0;
    const pages = Array.from(grid.querySelectorAll('[ref="cards[]"]'))
      .map((el) => Number(el.dataset.page))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!pages.length) return 0;
    return Math.min(...pages);
  }

  /**
   * Find the highest page number currently rendered.
   * This avoids relying on refs timing.
   * @returns {number}
   */
  #getCurrentMaxPage() {
    const grid = this.refs?.grid || this.querySelector('[ref="grid"]');
    if (!grid) return 0;
    const pages = Array.from(grid.querySelectorAll('[ref="cards[]"]'))
      .map((el) => Number(el.dataset.page))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!pages.length) return 0;
    return Math.max(...pages);
  }

  /**
   * iOS-safe: avoid :scope selector flakiness
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(this.#getPageCacheKey(page));
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    const all = Array.from(gridElement.querySelectorAll('[ref="cards[]"]'));
    return all.filter((el) => el.parentElement === gridElement);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#pageCacheNamespace = this.#getPageCacheNamespace();
    this.#contextVersion += 1;
    this.#renderedProductIds.clear();
    this.#renderedPages.clear();
    this.#lastRenderedPage = 0;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const { grid } = this.refs;
    if (grid) {
      const gridHeight = grid.getBoundingClientRect().height;
      if (gridHeight > 0) {
        grid.style.minHeight = `${gridHeight}px`;
      }
      grid.querySelectorAll('[ref="cards[]"]').forEach((card) => card.remove());
    }

    this.#updateHeavyCardsFlag();

    const observer = new MutationObserver(() => {
      finalize();
    });

    const finalize = () => {
      observer.disconnect();
      if (!this.isConnected) return;

      if (grid) {
        grid.style.minHeight = '';
        this.#seedRenderedState();
      }

      this.#observeViewMore();
      this.#fetchPage('next');
      requestIdleCallback(() => this.#runAutoloadCheck());
    };

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
        subtree: true,
      });

      // Fallback: ensure we resume even if data-last-page doesn't change
      setTimeout(finalize, 800);
    }
  };



  #detectIOS() {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isAppleTouch = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /iP(hone|od|ad)/.test(ua) || isAppleTouch;
  }

  #updateHeavyCardsFlag() {
    const { grid } = this.refs;
    if (!grid) return;
    const count = grid.querySelectorAll('[ref="cards[]"]').length;
    if (count >= this.#hoverDisableThreshold) {
      this.setAttribute('data-heavy-cards', 'true');
    } else {
      this.removeAttribute('data-heavy-cards');
    }
  }

  #shouldAutoloadNextPage() {
    if (this.#loading.next) return false;

    const nextPage = this.#getPage('next');
    if (!this.#shouldUsePage(nextPage)) return false;

    const { grid } = this.refs;
    if (!grid) return false;

    const viewportBottom = window.scrollY + (window.innerHeight || 0);
    const gridBottom = grid.getBoundingClientRect().bottom + window.scrollY;
    const margin = this.#getObserverMarginPx();

    return viewportBottom + margin >= gridBottom;
  }

  #shouldAutoloadPreviousPage() {
    if (this.#loading.previous) return false;

    const previousPage = this.#getPage('previous');
    if (!this.#shouldUsePage(previousPage)) return false;

    const { grid } = this.refs;
    if (!grid) return false;

    const viewportTop = window.scrollY;
    const gridTop = grid.getBoundingClientRect().top + window.scrollY;
    const margin = this.#getObserverMarginPx();

    return viewportTop - margin <= gridTop;
  }

  /**
   * Build a stable cache namespace by ignoring "page" from the query string.
   * This prevents reusing page-2 HTML from a different filter context.
   * @param {URL} [url]
   * @returns {string}
   */
  #getPageCacheNamespace(url = new URL(window.location.href)) {
    const normalizedUrl = new URL(url.toString());
    normalizedUrl.searchParams.delete('page');

    // Ensure deterministic ordering in cache key.
    normalizedUrl.searchParams.sort();

    const search = normalizedUrl.searchParams.toString();
    return `${normalizedUrl.pathname}?${search}`;
  }

  /**
   * @param {number} page
   * @param {string} [namespace]
   * @returns {string}
   */
  #getPageCacheKey(page, namespace = this.#pageCacheNamespace || this.#getPageCacheNamespace()) {
    return `${namespace}::${page}`;
  }

  #seedRenderedState() {
    const grid = this.refs?.grid || this.querySelector('[ref="grid"]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('[ref="cards[]"]'));
    cards.forEach((card) => {
      const pid = card.getAttribute('data-product-id');
      if (pid) this.#renderedProductIds.add(pid);
      const page = Number(card.getAttribute('data-page'));
      if (Number.isFinite(page) && page > 0) this.#renderedPages.add(page);
    });
  }

  /**
   * Remove duplicate products and track rendered products.
   * @param {Element[]} items
   * @returns {Element[]}
   */
  #dedupeAndTrack(items) {
    const deduped = [];
    for (const el of items) {
      const pid = el.getAttribute('data-product-id');
      if (pid) {
        if (this.#renderedProductIds.has(pid)) continue;
        this.#renderedProductIds.add(pid);
      }
      deduped.push(el);
    }
    return deduped;
  }
}
